/**
 * Types mirroring the Go API.
 *
 * Hand-written rather than generated, and that is a deliberate, stated deviation
 * from the spec, which calls for emitting OpenAPI from Go and generating this
 * file from it. That pipeline is the right end state — it turns backend/frontend
 * drift into a compile error instead of a production bug — but it needs
 * annotations across every handler plus a generator in CI, and shipping a
 * hand-written client now beats shipping neither.
 *
 * Until then the contract is enforced by the smoke test, which exercises the
 * real shapes end to end.
 */

export const ProjectHeader = 'X-Seidhi-Project';

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export interface ListResponse<T> {
  data: T[];
  next_cursor: string | null;
}

export type Channel = 'push' | 'email' | 'sms' | 'whatsapp';
export type Category = 'marketing' | 'product' | 'transactional' | 'billing' | 'security';

export interface Org {
  id: string;
  name: string;
  plan: string;
  role: 'owner' | 'member';
}

export interface User {
  id: string;
  email: string;
  name: string;
  orgs: Org[];
}

export interface Session {
  access_token: string;
  expires_at: string;
  user: User;
}

export interface QuietHours {
  start: string;
  end: string;
  respect_profile_tz: boolean;
}

export interface FrequencyCap {
  max_per_day?: number;
  max_per_week?: number;
  per_channel?: Record<string, number>;
  exempt_categories?: string[];
}

export interface Branding {
  display_name?: string;
  logo_url?: string;
  primary_color?: string;
  email_from_name?: string;
  email_from_address?: string;
  email_footer_html?: string;
  reply_to?: string;
  preference_center?: {
    custom_domain?: string;
    logo_url?: string;
    support_email?: string;
  };
}

export interface Project {
  id: string;
  org_id?: string;
  name: string;
  environment: string;
  timezone: string;
  branding: Branding;
  quiet_hours: QuietHours | null;
  frequency_cap: FrequencyCap | null;
  retention_days: number;
  created_at: string;
  role?: 'owner' | 'member';
}

export interface Profile {
  id: string;
  external_id: string | null;
  email: string | null;
  phone: string | null;
  timezone: string | null;
  locale: string | null;
  traits: Record<string, unknown>;
  first_seen_at: string;
  last_seen_at: string | null;
}

/** A behavioural event ingested via POST /v1/events — not an audit row. */
export interface TrackedEvent {
  id: number;
  profile_id: string | null;
  name: string;
  props: Record<string, unknown>;
  timestamp: string;
}

export interface Device {
  id: string;
  profile_id: string | null;
  platform: string;
  token: string;
  status: string;
  last_seen_at: string;
}

export interface Message {
  id: string;
  channel: Channel;
  status: 'queued' | 'sending' | 'completed' | 'failed';
  priority: number;
  source: string;
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  error?: string | null;
  scheduled_at?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface Delivery {
  id: number;
  profile_id: string | null;
  device_id: string | null;
  channel: string;
  address: string | null;
  status: string;
  error_code?: string | null;
  error_detail?: string | null;
  provider_id?: string | null;
  attempts: number;
  /**
   * Push providers report acceptance, never delivery. `delivered_at` stays null
   * for push forever, and the UI says so rather than quietly showing a dash.
   */
  accepted_at: string | null;
  delivered_at: string | null;
  opened_at?: string | null;
  clicked_at?: string | null;
}

export interface Template {
  id: string;
  name: string;
  channel: Channel;
  category: Category;
  subject: string | null;
  preheader: string | null;
  body: string;
  html: string | null;
  engine: string;
  version: number;
  variables: unknown;
  dlt_template_id: string | null;
  dlt_sender_id: string | null;
  dlt_status: 'not_required' | 'pending' | 'approved' | 'rejected';
  dlt_rejection_reason: string | null;
  provider_template_name: string | null;
  updated_at: string;
  sms_segments?: number;
  sms_unicode?: boolean;
  dlt_placeholders?: number;
}

export interface Segment {
  id: string;
  name: string;
  description: string;
  definition: SegmentNode;
  kind: 'computed' | 'materialized';
  member_count: number | null;
  last_computed_at: string | null;
  compile_error: string | null;
  summary: string;
  updated_at: string;
}

export type SegmentOp =
  | 'and' | 'or' | 'not'
  | 'trait' | 'profile' | 'event' | 'device' | 'segment' | 'consent';

export type SegmentCmp =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'contains' | 'starts_with' | 'ends_with'
  | 'exists' | 'not_exists' | 'in' | 'not_in';

export interface SegmentNode {
  op: SegmentOp;
  children?: SegmentNode[];
  path?: string;
  field?: string;
  cmp?: SegmentCmp;
  value?: unknown;
  values?: unknown[];
  type?: 'string' | 'number' | 'bool' | 'date';
  name?: string;
  count?: number;
  within?: string;
  platform?: string;
  segment_id?: string;
  category?: string;
  channel?: string;
  opted_in?: boolean;
}

export interface SegmentPreview {
  count: number;
  summary: string;
  sample: Array<{
    id: string;
    external_id: string | null;
    email: string | null;
    phone: string | null;
    traits: Record<string, unknown>;
  }>;
}

export type JourneyNodeType = 'send' | 'wait' | 'wait_until' | 'branch' | 'split' | 'exit';

export interface JourneyNode {
  type: JourneyNodeType;
  channel?: Channel;
  template_id?: string;
  category?: Category;
  priority?: number;
  payload?: Record<string, unknown>;
  duration?: string;
  at?: string;
  time_of_day?: string;
  days_of_week?: number[];
  condition?: SegmentNode;
  true?: string;
  false?: string;
  variants?: Array<{ name?: string; weight: number; next: string }>;
  next?: string;
}

export interface JourneyDefinition {
  entry: string;
  nodes: Record<string, JourneyNode>;
}

export interface JourneyTrigger {
  type: 'event' | 'segment_enter' | 'manual';
  name?: string;
  segment_id?: string;
  filter?: SegmentNode;
}

export interface Journey {
  id: string;
  name: string;
  definition: JourneyDefinition;
  trigger: JourneyTrigger;
  status: 'draft' | 'live' | 'paused' | 'archived';
  entry_rule: 'once' | 'reentrant';
  entry_cap: number | null;
  max_nodes: number;
  dry_run: boolean;
  version: number;
  activated_at: string | null;
  updated_at: string;
  created_at: string;
}

export interface JourneyDetail {
  journey: Journey;
  runs: Record<string, number>;
  node_stats: Array<{
    node_id: string;
    version: number;
    entered: number;
    completed: number;
    exited: number;
  }>;
}

export interface Integration {
  id: string;
  channel: Channel;
  provider: string;
  config: Record<string, unknown>;
  status: string;
  key_version: number;
  verified_at: string | null;
  updated_at: string;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scope: 'public' | 'secret';
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
  key?: string;
}

export interface AuditEvent {
  id: number;
  actor_type: string;
  actor_id: string;
  actor_label: string | null;
  action: string;
  resource: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface Suppression {
  channel: string;
  address: string;
  reason: string;
  created_at: string;
}

export interface Preference {
  category: string;
  channel: string;
  opted_in: boolean;
  source: string | null;
  consented_at: string | null;
  consent_source: string | null;
  updated_at: string;
}

export interface Overview {
  window_days: number;
  profiles: number;
  suppressions: number;
  deliveries: Array<{ channel: string; status: string; total: number }> | null;
  errors: Array<{ code: string; channel: string; total: number }> | null;
  devices: Array<{ platform: string; status: string; total: number }> | null;
  events: Array<{ name: string; total: number }> | null;
  daily: Array<{
    day: string;
    channel: string;
    sent: number;
    delivered: number;
    failed: number;
    opened: number;
    clicked: number;
  }>;
}

export interface MessageFunnel {
  total: number;
  sent: number;
  accepted: number;
  delivered: number;
  opened: number;
  clicked: number;
  failed: number;
  skipped: number;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'member';
  status: string;
  last_login_at: string | null;
  created_at: string;
}

export interface ProfileDetail {
  profile: Profile;
  devices: Device[];
  events: Array<{ name: string; props: Record<string, unknown>; timestamp: string }>;
  deliveries: Delivery[];
  preferences: Preference[];
}
